import { Channel, User, UserFactory } from '@relewise/client';
import { checkIfServiceConsentIsGiven, CLIENT_COOKIE_NAME, getCookie, hasUCUILoaded } from '../../shared/functions/cookie';

const DEVICE_COOKIE_VALUE_FOR_MOBILE_APP = 'webview';

class RelewiseUserService {
    private getUsercentricsControllerId() {
        if (!hasUCUILoaded(window)) {
            return;
        }
        return window.UC_UI.getControllerId();
    }

    private async buildUnauthenticatedUser() {
        if (!hasUCUILoaded(window)) {
            return UserFactory.anonymous();
        }

        const hasConsented = await checkIfServiceConsentIsGiven('Relewise');
        const controllerId = this.getUsercentricsControllerId();

        return hasConsented && controllerId ? UserFactory.byTemporaryId(controllerId) : UserFactory.anonymous();
    }

    private buildAuthenticatedUser() {
        if (!window.___initialData___) {
            console.error('[Relewise User Service] Could not load Relewise Authenticated user: No initial data object in window');
            return;
        }

        const authUser = window.___initialData___.requestContext?.user;
        const { isAuthenticated, customerAccountNumber, emailAddress } = authUser ?? {};
        if (!isAuthenticated || !customerAccountNumber || !emailAddress) {
            return;
        }

        return UserFactory.byAuthenticatedId(customerAccountNumber, this.getUsercentricsControllerId(), {
            email: emailAddress
        });
    }

    private getDeviceType() {
        const ua = navigator.userAgent;

        // iPad needs a special check: it sometimes pretends to be a mac when used in desktop mode
        if (/iPad/.test(ua) || (/Mac/.test(ua) && navigator.maxTouchPoints > 1)) {
            return 'Tablet';
        }
        if (/Android/.test(ua) && !/Mobile/.test(ua)) {
            return 'Tablet';
        }
        if (/Android|iPhone/.test(ua)) {
            return 'Phone';
        }

        return 'Desktop';
    }

    private getPlatform() {
        const ua = navigator.userAgent;

        // MUST check for iPad before checking for Mac! iPad UA can have the word 'Mac' in it as well.
        if (/iPhone|iPad/i.test(ua)) {
            return 'iOS';
        }
        if (/Mac/i.test(ua)) {
            return 'macOS';
        }
        if (/android/i.test(ua)) {
            return 'Android';
        }
        if (/Windows/i.test(ua)) {
            return 'Windows';
        }
        if (/Linux/i.test(ua)) {
            return 'Linux';
        }

        return 'Unknown';
    }

    private getUserChannel(): Channel | undefined {
        const deviceType = this.getDeviceType();
        const platform = this.getPlatform();
        // Name of the browser, or 'webview' when in mobile app
        const clientCookieValue = getCookie(CLIENT_COOKIE_NAME);
        const clientName = clientCookieValue === DEVICE_COOKIE_VALUE_FOR_MOBILE_APP ? 'App' : clientCookieValue;

        return {
            name: deviceType,
            subChannel: {
                name: platform,
                subChannel: {
                    name: clientName
                }
            }
        };
    }

    private getUserClassifications(): Record<string, string | null> | undefined {
        if (!window.___initialData___) {
            return;
        }

        return {
            country: window.___initialData___.requestContext.channel?.ChannelCountryRegionISOCode ?? null
        };
    }

    public async getUser() {
        // Do not run any relewise operations during SSR, but return an anonymous user so that this funcion always returns a user
        if (typeof window === 'undefined') {
            return UserFactory.anonymous();
        }

        let user: User;

        const authenticatedUser = this.buildAuthenticatedUser();
        if (authenticatedUser) {
            user = authenticatedUser;
        } else {
            user = await this.buildUnauthenticatedUser();
        }

        user.channel = this.getUserChannel();
        user.classifications = this.getUserClassifications();

        return user;
    }
}

export const relewiseUserService = new RelewiseUserService();
